import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Editor, General } from 'icons/untitledui';

import { usePosthogCapture } from 'shiftNotes/posthogCapture/shiftNotes';
import { useShiftNoteCapture } from 'shiftNotes/helpers/ShiftNoteCaptureProvider';
import useNoAudioWarning from 'shiftNotes/hooks/useNoAudioWarning';
import useNoteAttachmentPicker from 'shiftNotes/hooks/useNoteAttachmentPicker';
import useRecordingDuration from 'shiftNotes/hooks/useRecordingDuration';
import * as Buttons from 'theme/components/Buttons.style';
import * as Styled from 'shiftNotes/styles/NoteCaptureRecording.style';
import NoteCaptureActionCard from './NoteCaptureActionCard';
import NoteCaptureAttachmentsPreview from './NoteCaptureAttachmentsPreview';
import RecordingPausedChip from './RecordingPausedChip';
import Waveform from './Waveform';

const AUDIO_THRESHOLD = 0.15;
const SWITCH_TO_WRITING_CONFIRM_THRESHOLD_MS = 4000;

export interface NoteCaptureError {
  type: 'media' | 'upload';
  message?: string;
}

export interface NoteCaptureRecordingProps {
  onCancel: () => void;
  onConfirm: () => void;
  onSwitchToWriting?: () => void;
  onPause?: () => void;
  onResume?: () => void;
  audioLevel?: number;
  isAudioCapture?: boolean;
  isPaused?: boolean;
  inline?: boolean;
}

export default function NoteCaptureRecording({
  onCancel,
  onConfirm,
  onSwitchToWriting,
  onPause,
  onResume,
  audioLevel = 0,
  isAudioCapture = true,
  isPaused = false,
  inline = false,
}: NoteCaptureRecordingProps): JSX.Element {
  const { t } = useTranslation('pageShiftNotes');
  const shiftNotesCapture = usePosthogCapture();
  const { attachments, isResolveMode } = useShiftNoteCapture();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingSwitchToWriting, setPendingSwitchToWriting] = useState(false);
  const [hasCapturedAudio, setHasCapturedAudio] = useState(false);

  const isSpeaking = !isPaused && audioLevel > AUDIO_THRESHOLD;

  useEffect(() => {
    if (isSpeaking) setHasCapturedAudio(true);
  }, [isSpeaking]);

  const getRecordingDurationMs = useRecordingDuration(isPaused);

  const showNoAudioWarning = useNoAudioWarning({ isSpeaking, isPaused });
  const feedbackText =
    !isPaused && showNoAudioWarning
      ? t('noteCaptureRecording.noAudioWarning')
      : null;

  const { attachmentError, handleRemoveAttachment, controls } =
    useNoteAttachmentPicker({
      fromState: 'paused',
      onBeforePick: () => {
        if (!isPaused && onPause) {
          onPause();
          shiftNotesCapture.captureRecordingPaused({ reason: 'attachment' });
        }
      },
    });

  const handleCancelClick = () => {
    setPendingSwitchToWriting(false);
    setShowConfirmation(true);
  };

  const handleKeepRecording = () => {
    setPendingSwitchToWriting(false);
    setShowConfirmation(false);
  };

  const handleDiscardRecording = () => {
    setShowConfirmation(false);
    if (pendingSwitchToWriting && onSwitchToWriting) {
      setPendingSwitchToWriting(false);
      onSwitchToWriting();
      return;
    }
    onCancel();
  };

  const handleSwitchToWritingClick = () => {
    if (!onSwitchToWriting) return;
    const isShortRecording =
      getRecordingDurationMs() < SWITCH_TO_WRITING_CONFIRM_THRESHOLD_MS;
    if (!hasCapturedAudio || isShortRecording) {
      onSwitchToWriting();
      return;
    }
    setPendingSwitchToWriting(true);
    setShowConfirmation(true);
  };

  const handleResumeClick = () => {
    if (!onResume) return;
    onResume();
    shiftNotesCapture.captureRecordingResumed();
  };

  if (showConfirmation && pendingSwitchToWriting) {
    return (
      <NoteCaptureActionCard
        title={t('noteCaptureRecording.switchConfirm.title')}
        description={t('noteCaptureRecording.switchConfirm.message')}
        secondaryLabel={t('noteCaptureRecording.switchConfirm.keepRecording')}
        onSecondaryClick={handleKeepRecording}
        primaryLabel={t('noteCaptureRecording.switchConfirm.switch')}
        onPrimaryClick={handleDiscardRecording}
        inline={inline}
      />
    );
  }

  if (showConfirmation) {
    return (
      <NoteCaptureActionCard
        title={t('noteCaptureRecording.cancelConfirm.title')}
        description={t('noteCaptureRecording.cancelConfirm.message')}
        secondaryLabel={t('noteCaptureRecording.cancelConfirm.keepRecording')}
        onSecondaryClick={handleKeepRecording}
        primaryLabel={t('noteCaptureRecording.cancelConfirm.discard')}
        onPrimaryClick={handleDiscardRecording}
        inline={inline}
      />
    );
  }

  return (
    <Styled.Card $inline={inline}>
      {isAudioCapture && (
        <Styled.TopBar>
          <Buttons.SecondaryLightIcon
            $size="medium"
            onClick={handleCancelClick}
            aria-label={t('noteCaptureRecording.cancel')}
            data-testid="note-capture-cancel-button"
          >
            <General.XClose />
          </Buttons.SecondaryLightIcon>
        </Styled.TopBar>
      )}
      <Styled.ContentSection>
        {feedbackText && (
          <Styled.FeedbackText>{feedbackText}</Styled.FeedbackText>
        )}
        {!feedbackText && isResolveMode && (
          <Styled.ResolveHint>
            {t('noteCapture.resolveHint')}
          </Styled.ResolveHint>
        )}
        {isPaused ? (
          <RecordingPausedChip onResume={handleResumeClick} />
        ) : (
          <Waveform audioLevel={audioLevel} isAnimating={isSpeaking} />
        )}
      </Styled.ContentSection>
      {attachments.length > 0 && (
        <Styled.AttachmentsRow>
          <NoteCaptureAttachmentsPreview
            files={attachments}
            onRemove={handleRemoveAttachment}
          />
        </Styled.AttachmentsRow>
      )}
      {attachmentError && (
        <Styled.AttachmentError role="alert">
          {t(attachmentError.key, attachmentError.params)}
        </Styled.AttachmentError>
      )}
      {isAudioCapture && (
        <Styled.ControlsSection>
          <Styled.ControlsLeft>
            {controls}
            {onSwitchToWriting && (
              <Buttons.SecondaryLightIcon
                $size="medium"
                onClick={handleSwitchToWritingClick}
                aria-label={t('noteCaptureModeToggle.switchToText')}
                data-testid="note-capture-mode-text"
              >
                <Editor.Keyboard01 />
              </Buttons.SecondaryLightIcon>
            )}
          </Styled.ControlsLeft>
          <Buttons.PrimaryIcon
            $size="medium"
            onClick={onConfirm}
            aria-label={t('noteCaptureRecording.confirm')}
            data-testid="note-capture-stop-button"
          >
            <General.Check />
          </Buttons.PrimaryIcon>
        </Styled.ControlsSection>
      )}
    </Styled.Card>
  );
}
